import React from 'react';
import { 
  Shield, 
  TrendingUp, 
  Globe, 
  Clock, 
  Lock,
  Cpu,
  Target,
  RefreshCw,
  AlertTriangle,
  Bot,
  Users
} from 'lucide-react';
import './Features.scss';

const Features: React.FC = () => {
  const features = [
    {
      icon: Bot,
      title: 'Automated AI Trading Bot',
      description: 'Fully autonomous trading bot with advanced ML algorithms that execute trades 24/7 without human intervention.',
      color: 'primary'
    },
    {
      icon: Users,
      title: 'AI Trading Assistant',
      description: 'Intelligent co-pilot for manual traders providing real-time insights, trade suggestions, and risk analysis.',
      color: 'info'
    },
    {
      icon: Shield,
      title: 'Bank-Grade Security',
      description: 'Military-grade encryption and multi-layer security protocols protect your assets 24/7.',
      color: 'success'
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Analytics',
      description: 'Get comprehensive market insights with live charts, indicators, and performance metrics.',
      color: 'warning'
    },
    {
      icon: Globe,
      title: 'Global Markets',
      description: 'Trade on 50+ exchanges worldwide with unified portfolio management and reporting.',
      color: 'info'
    },
    {
      icon: Lock,
      title: 'Advanced Portfolio',
      description: 'Sophisticated portfolio optimization with risk management and diversification strategies.',
      color: 'success'
    }
  ];

  const advancedFeatures = [
    {
      icon: Cpu,
      title: 'Virtualized Infrastructure',
      description: 'Cloud-native architecture ensures 99.9% uptime with automatic failover and scaling.',
      highlight: 'High Performance'
    },
    {
      icon: Target,
      title: 'Smart Order Routing',
      description: 'Intelligent order splitting and routing to minimize slippage and maximize execution quality.',
      highlight: 'Optimized Execution'
    },
    {
      icon: RefreshCw,
      title: 'Automated Rebalancing',
      description: 'Dynamic portfolio rebalancing based on market conditions and predefined strategies.',
      highlight: 'Smart Management'
    },
    {
      icon: AlertTriangle,
      title: 'Risk Management',
      description: 'Comprehensive risk controls with stop-loss, position sizing, and exposure limits.',
      highlight: 'Protected Trading'
    }
  ];

  return (
    <section className="features section bg-gray-50">
      <div className="container">
        {/* Section Header */}
        <div className="features__header text-center">
          <h2 className="features__title">
            Automated & Assisted Trading Features for
            <span className="gradient-text"> Every Trading Style</span>
          </h2>
          <p className="features__subtitle">
            CoinVAT offers both fully automated AI trading bots and intelligent trading assistance, 
            combining cutting-edge technology with professional-grade tools.
          </p>
        </div>

        {/* Main Features Grid */}
        <div className="features__grid">
          {features.map((feature, index) => (
            <div key={index} className="features__card">
              <div className={`features__icon features__icon--${feature.color}`}>
                <feature.icon size={28} />
              </div>
              <h3 className="features__card-title">{feature.title}</h3>
              <p className="features__card-description">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Advanced Features Section */}
        <div className="features__advanced">
          <div className="features__advanced-header">
            <h3 className="features__advanced-title">
              Advanced Bot & Assisted Trading Capabilities
            </h3>
            <p className="features__advanced-subtitle">
              Professional-grade features designed for both automated bot trading and AI-assisted manual trading.
            </p>
          </div>

          <div className="features__advanced-grid">
            {advancedFeatures.map((feature, index) => (
              <div key={index} className="features__advanced-card">
                <div className="features__advanced-content">
                  <div className="features__advanced-icon">
                    <feature.icon size={24} />
                  </div>
                  <div className="features__advanced-info">
                    <div className="features__advanced-highlight">
                      {feature.highlight}
                    </div>
                    <h4 className="features__advanced-card-title">
                      {feature.title}
                    </h4>
                    <p className="features__advanced-description">
                      {feature.description}
                    </p>
                  </div>
                </div>
                <div className="features__advanced-decoration"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Stats */}
        <div className="features__stats">
          <div className="features__stats-content">
            <h3 className="features__stats-title">Proven Performance</h3>
            <div className="features__stats-grid">
              <div className="features__stat">
                <div className="features__stat-icon">
                  <Clock size={24} />
                </div>
                <div className="features__stat-info">
                  <div className="features__stat-number">0.1ms</div>
                  <div className="features__stat-label">Average Latency</div>
                </div>
              </div>
              <div className="features__stat">
                <div className="features__stat-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="features__stat-info">
                  <div className="features__stat-number">94.3%</div>
                  <div className="features__stat-label">Success Rate</div>
                </div>
              </div>
              <div className="features__stat">
                <div className="features__stat-icon">
                  <Lock size={24} />
                </div>
                <div className="features__stat-info">
                  <div className="features__stat-number">100%</div>
                  <div className="features__stat-label">Security Score</div>
                </div>
              </div>
              <div className="features__stat">
                <div className="features__stat-icon">
                  <RefreshCw size={24} />
                </div>
                <div className="features__stat-info">
                  <div className="features__stat-number">24/7</div>
                  <div className="features__stat-label">Active Trading</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
